import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import type {
  ApprovalApplication,
  ApprovalDecision,
  ApprovalRequest,
  ApprovalWorkflow,
} from "@/pages/admin/approvals/hooks/useApprovals";

interface ApprovalRequestPanelProps {
  requests: ApprovalRequest[];
  decisions: ApprovalDecision[];
  applications: ApprovalApplication[];
  workflows: ApprovalWorkflow[];
  loading: boolean;
  onStart: (applicationId: number) => Promise<void>;
  onDecide: (requestId: number, decision: "approved" | "rejected", note: string) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

const STATUS_TONE: Record<string, string> = {
  pending: "bg-secondary-100 text-secondary-900",
  approved: "bg-primary-100 text-primary-700",
  rejected: "bg-accent-100 text-accent-800",
};

// 多级审批：待发起列表（给待审批申请发起审批链）+ 进行中/已结束的审批请求，
// 每条请求展示逐级进度链与决策记录，并在当前级提供通过 / 驳回操作。
export default function ApprovalRequestPanel({
  requests,
  decisions,
  applications,
  workflows,
  loading,
  onStart,
  onDecide,
  notify,
}: ApprovalRequestPanelProps) {
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [decideTarget, setDecideTarget] = useState<{ requestId: number; decision: "approved" | "rejected" } | null>(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const appById = useMemo(() => {
    const map = new Map<number, ApprovalApplication>();
    applications.forEach((app) => map.set(app.id, app));
    return map;
  }, [applications]);

  const wfById = useMemo(() => {
    const map = new Map<number, ApprovalWorkflow>();
    workflows.forEach((wf) => map.set(wf.id, wf));
    return map;
  }, [workflows]);

  const decisionsByRequest = useMemo(() => {
    const map = new Map<number, ApprovalDecision[]>();
    decisions.forEach((decision) => {
      const list = map.get(decision.request_id) ?? [];
      list.push(decision);
      map.set(decision.request_id, list);
    });
    return map;
  }, [decisions]);

  const startedIds = useMemo(() => new Set(requests.map((request) => request.application_id)), [requests]);
  const pendingStart = useMemo(
    () => applications.filter((app) => !startedIds.has(app.id)),
    [applications, startedIds],
  );

  const sortedRequests = useMemo(() => {
    const weight = (status: string) => (status === "pending" ? 0 : 1);
    return [...requests].sort((a, b) => {
      if (weight(a.status) !== weight(b.status)) return weight(a.status) - weight(b.status);
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [requests]);

  const statusLabel = (status: string) =>
    status === "approved"
      ? t("approvals.statusApproved")
      : status === "rejected"
        ? t("approvals.statusRejected")
        : t("approvals.statusPending");

  const handleStart = async (applicationId: number) => {
    setBusyId(applicationId);
    try {
      await onStart(applicationId);
      notify(t("approvals.startSuccess"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDecide = async () => {
    if (!decideTarget) return;
    setSubmitting(true);
    try {
      await onDecide(decideTarget.requestId, decideTarget.decision, note.trim());
      notify(decideTarget.decision === "approved" ? t("approvals.approveSuccess") : t("approvals.rejectSuccess"), "success");
      setDecideTarget(null);
      setNote("");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* 待发起审批 */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
            <i className="ri-send-plane-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("approvals.pendingStartTitle")}</h2>
            <p className="text-[11px] text-foreground-500">{t("approvals.pendingStartSub")}</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 h-16 rounded-md bg-background-100" />
        ) : pendingStart.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-6 text-center text-xs text-foreground-500">
            {t("approvals.pendingStartEmpty")}
          </p>
        ) : (
          <ul className="mt-4 flex flex-col gap-2">
            {pendingStart.slice(0, 12).map((app) => (
              <li
                key={app.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 px-3.5 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground-950">
                    {app.product_name}
                    {app.product_version ? ` · ${app.product_version}` : ""}
                  </p>
                  <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                    {app.applicant_name}
                    {app.email ? ` · ${app.email}` : ""} · {t("approvals.seatUnit", { count: app.seat_count })}
                    {app.renew_license_id ? ` · ${t("approvals.renewalTag")}` : ""}
                  </p>
                  <p className="mt-0.5 text-[11px] text-foreground-400">
                    {t("approvals.submittedAt", { date: formatFullDate(app.created_at) })}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busyId === app.id}
                  onClick={() => void handleStart(app.id)}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className={busyId === app.id ? "ri-loader-4-line animate-spin" : "ri-play-line"} />
                  </span>
                  {t("approvals.startApproval")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 审批请求 */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-git-branch-line" />
            </span>
            <div>
              <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("approvals.requestsTitle")}</h2>
              <p className="text-[11px] text-foreground-500">{t("approvals.requestsSub")}</p>
            </div>
          </div>
          <span className="rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
            {t("approvals.requestsCount", { count: sortedRequests.length })}
          </span>
        </div>

        {loading ? (
          <div className="mt-4 flex flex-col gap-2">
            {[0, 1].map((item) => (
              <div key={item} className="h-24 rounded-md bg-background-100" />
            ))}
          </div>
        ) : sortedRequests.length === 0 ? (
          <p className="mt-4 rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-8 text-center text-xs text-foreground-500">
            {t("approvals.requestsEmpty")}
          </p>
        ) : (
          <ul className="mt-4 flex flex-col gap-3">
            {sortedRequests.map((request) => {
              const app = appById.get(request.application_id) ?? null;
              const workflow = request.workflow_id ? wfById.get(request.workflow_id) ?? null : null;
              const steps = workflow?.steps ?? [];
              const reqDecisions = decisionsByRequest.get(request.id) ?? [];
              const decisionByStep = new Map(reqDecisions.map((item) => [item.step_order, item]));
              const currentStep = steps.find((step) => step.step_order === request.current_step) ?? null;
              const isPending = request.status === "pending";
              const startedMs = new Date(request.step_started_at ?? request.created_at).getTime();
              const overdueHours = Math.max(0, Math.floor((Date.now() - startedMs) / 3600000));
              const slaHours = Number(currentStep?.sla_hours ?? 24);
              return (
                <li key={request.id} className="rounded-md border border-background-200 bg-background-100 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-foreground-950">
                          {app?.product_name ?? `#${request.application_id}`}
                        </p>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[request.status] ?? "bg-background-200 text-foreground-600"}`}
                        >
                          {statusLabel(request.status)}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                        {app?.applicant_name ?? "—"}
                        {app?.email ? ` · ${app.email}` : ""}
                        {workflow ? ` · ${workflow.name}` : ""}
                      </p>
                    </div>
                    <span className="text-[11px] text-foreground-400">
                      {formatRelativeTime(request.created_at)}
                    </span>
                  </div>

                  {/* 逐级进度链 */}
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {steps.map((step, index) => {
                      const decision = decisionByStep.get(step.step_order);
                      const done = Boolean(decision);
                      const isCurrent = isPending && step.step_order === request.current_step;
                      const rejectedHere = decision?.decision === "rejected";
                      return (
                        <span key={step.id} className="inline-flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] ${
                              rejectedHere
                                ? "bg-accent-100 text-accent-800"
                                : done
                                  ? "bg-primary-100 text-primary-700"
                                  : isCurrent
                                    ? "bg-secondary-500 text-background-50"
                                    : "border border-background-300 bg-background-50 text-foreground-500"
                            }`}
                          >
                            <span className="flex h-3.5 w-3.5 items-center justify-center">
                              <i
                                className={
                                  rejectedHere
                                    ? "ri-close-line"
                                    : done
                                      ? "ri-check-line"
                                      : isCurrent
                                        ? "ri-more-line"
                                        : "ri-time-line"
                                }
                              />
                            </span>
                            {step.name}
                          </span>
                          {index < steps.length - 1 && (
                            <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
                              <i className="ri-arrow-right-s-line" />
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>

                  {/* 决策记录 */}
                  {reqDecisions.length > 0 && (
                    <ul className="mt-3 flex flex-col gap-1.5">
                      {reqDecisions.map((decision) => (
                        <li key={decision.id} className="flex flex-wrap items-center gap-2 text-[11px]">
                          <span
                            className={`flex h-4 w-4 items-center justify-center rounded-full ${
                              decision.decision === "approved"
                                ? "bg-primary-100 text-primary-700"
                                : "bg-accent-100 text-accent-800"
                            }`}
                          >
                            <i className={decision.decision === "approved" ? "ri-check-line" : "ri-close-line"} />
                          </span>
                          <span className="font-medium text-foreground-800">{decision.step_name}</span>
                          <span className="text-foreground-500">
                            {decision.approver_name ?? t("approvals.unknown")}
                          </span>
                          <span className="text-foreground-400">{formatFullDate(decision.created_at)}</span>
                          {decision.note && <span className="text-foreground-600">· {decision.note}</span>}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* 当前级操作 */}
                  {isPending && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] text-foreground-700">
                          {t("approvals.currentStep")}：
                          <span className="font-medium text-secondary-900">{currentStep?.name ?? `#${request.current_step}`}</span>
                          <span className="ml-1.5 text-foreground-400">
                            ({(request.current_step)}/{steps.length})
                          </span>
                        </span>
                        {request.escalated_at && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
                            <i className="ri-arrow-up-double-line" />
                            {t("approvals.escalatedTag")}
                          </span>
                        )}
                        {!request.escalated_at && slaHours > 0 && overdueHours >= slaHours && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                            <i className="ri-alarm-warning-line" />
                            {t("approvals.overdueTag", { hours: overdueHours })}
                          </span>
                        )}
                        {request.reminded_at && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-background-200 px-2 py-0.5 text-[10px] font-medium text-foreground-600">
                            <i className="ri-notification-3-line" />
                            {t("approvals.remindedTag")}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setDecideTarget({ requestId: request.id, decision: "rejected" });
                            setNote("");
                          }}
                          className="cursor-pointer whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-100"
                        >
                          {t("approvals.reject")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDecideTarget({ requestId: request.id, decision: "approved" });
                            setNote("");
                          }}
                          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                        >
                          {t("approvals.approve")}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {decideTarget && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-foreground-950/40 px-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2">
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-md ${
                  decideTarget.decision === "approved" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"
                }`}
              >
                <i className={decideTarget.decision === "approved" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950">
                {decideTarget.decision === "approved" ? t("approvals.approveTitle") : t("approvals.rejectTitle")}
              </h3>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-foreground-600">
              {decideTarget.decision === "approved" ? t("approvals.approveDesc") : t("approvals.rejectDesc")}
            </p>
            <label className="mt-3 block text-xs font-medium text-foreground-700">{t("approvals.noteLabel")}</label>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              maxLength={300}
              placeholder={t("approvals.notePlaceholder")}
              className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => setDecideTarget(null)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("approvals.cancel")}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void confirmDecide()}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-background-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  decideTarget.decision === "approved" ? "bg-primary-500 hover:bg-primary-600" : "bg-accent-600 hover:bg-accent-700"
                }`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-check-line"} />
                </span>
                {t("approvals.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}